import {
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Link as ChakraLink,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
} from '@chakra-ui/react';
import {skipToken} from '@reduxjs/toolkit/dist/query';
import {addMinutes} from 'date-fns';
import {useEffect, useState} from 'react';
import {useGetCourtsForEventQuery, useGetGameByIdQuery, useUpdateGameMutation,} from '../app/api';
import {TimeInput} from './TimeInput';
import {useOrganizationContext} from 'contexts/OrganizationContext';

interface PlayoffGameModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  gameId: number;
}

export const PlayoffGameModal: React.FC<PlayoffGameModalProps> = ({
                                                                    isOpen,
                                                                    onClose,
                                                                    eventId,
                                                                    gameId,
                                                                  }) => {
  const {currentOrg} = useOrganizationContext();
  const {data: courts} = useGetCourtsForEventQuery(eventId);
  const {data: game, isLoading: gameLoading} = useGetGameByIdQuery(
    isOpen ? gameId : skipToken
  );

  const [newCourtId, setNewCourtId] = useState<number | undefined>(undefined);
  const [newTime, setNewTime] = useState<Date>(game?.startTime || new Date());
  const [updateGame, {isLoading: updateLoading}] = useUpdateGameMutation();

  useEffect(() => {
    if (game) {
      setNewCourtId(game.courtId);
      setNewTime(new Date(game.startTime));
    }
  }, [game]);

  const submitForm = () => {
    updateGame({
      id: gameId,
      game: {
        courtId: newCourtId,
        startTime: newTime,
        endTime: addMinutes(newTime, 20),
      },
    });
  };

  const scoringUrl = currentOrg
    ? `/organizations/${currentOrg.id}/events/${eventId}/scoringV2/${gameId}`
    : '/';

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay/>
      <ModalContent>
        <ModalHeader>
          <HStack>
            <Heading size="md">Playoff Game</Heading>


            <ChakraLink
              href={scoringUrl}
              isExternal
              _hover={{textDecoration: 'none'}}
            >
              <Button>
                Score
              </Button>
            </ChakraLink>
          </HStack>
        </ModalHeader>

        <ModalCloseButton/>
        {gameLoading ? (
          <Spinner />
        ) : (
          <ModalBody>
            <Heading size="sm" mb="2" textAlign="center">
              {game?.homeTeamName} vs. {game?.awayTeamName ?? '-----'}
            </Heading>
            <FormControl>
              <FormLabel>Change Court</FormLabel>
              <Select
                value={newCourtId}
                onChange={(e) => setNewCourtId(parseInt(e.target.value))}
              >
                <option value="" disabled selected>
                  Select a Court
                </option>
                {courts?.map((court) => (
                  <option key={court.id} value={court.id}>
                    {court.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Change Time</FormLabel>
              <TimeInput date={newTime} onTimeChange={setNewTime}/>
            </FormControl>
          </ModalBody>
        )}

        <ModalFooter>
          <Button variantColor="blue" mr={3} onClick={onClose}>
            Close
          </Button>
          <Button
            isLoading={updateLoading}
            variantColor="green"
            onClick={submitForm}
            isDisabled={newCourtId == null}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
